import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControlLabel,
  Switch,
  Typography,
} from '@mui/material';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import { useNativeDisplayContext } from './NativeDisplayProvider';
import {
  checkBiometricAvailability,
  isBiometricUnlockEnabled,
  markBiometricSessionUnlocked,
  setBiometricEnablePromptDismissed,
  setBiometricUnlockEnabled,
  verifyBiometricUnlock,
} from '../../utils/biometricUnlock';

const NativeBiometricSettings: React.FC = () => {
  const { isNativeShell } = useNativeDisplayContext();
  const [enabled, setEnabled] = useState(() => isBiometricUnlockEnabled());
  const [available, setAvailable] = useState<boolean | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  useEffect(() => {
    if (!isNativeShell) return;
    let cancelled = false;
    void (async () => {
      const result = await checkBiometricAvailability();
      if (!cancelled) setAvailable(result.available);
    })();
    return () => {
      cancelled = true;
    };
  }, [isNativeShell]);

  if (!isNativeShell) {
    return null;
  }

  const handleToggle = async (_: React.ChangeEvent<HTMLInputElement>, checked: boolean) => {
    setBusy(true);
    setNote(null);
    try {
      if (checked) {
        if (available === false) {
          setNote('Biometrics are not available or not enrolled on this device.');
          return;
        }
        const ok = await verifyBiometricUnlock();
        if (!ok) {
          setNote('Biometric verification failed. Unlock was not enabled.');
          return;
        }
        setBiometricUnlockEnabled(true);
        setBiometricEnablePromptDismissed(true);
        markBiometricSessionUnlocked();
        setEnabled(true);
        setNote('Fingerprint / face unlock enabled for this device.');
      } else {
        setBiometricUnlockEnabled(false);
        setEnabled(false);
        setNote('Fingerprint / face unlock turned off.');
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Card elevation={3} sx={{ mb: 3 }} id="biometric-unlock-settings">
      <CardContent>
        <Typography variant="h5" sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <FingerprintIcon color="primary" />
          App unlock
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Use this device&apos;s fingerprint or face unlock to reopen Qixit Mail while your session is
          still valid. This does not replace password sign-in after logout or when the session expires.
          Authenticator app (TOTP) is optional and not required to enable this.
        </Typography>

        {available === false && (
          <Alert severity="info" sx={{ mb: 2 }}>
            Biometrics are not available on this device. Enroll a fingerprint or face unlock in system
            settings first.
          </Alert>
        )}

        {note && (
          <Alert severity="info" sx={{ mb: 2 }} onClose={() => setNote(null)}>
            {note}
          </Alert>
        )}

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={(e, checked) => void handleToggle(e, checked)}
                disabled={busy || available === false}
              />
            }
            label="Unlock with fingerprint or face"
          />
          {busy && <CircularProgress size={20} />}
        </Box>
      </CardContent>
    </Card>
  );
};

export default NativeBiometricSettings;
